import { GlowFilter, MovieClip, TextField, TextFormat } from "../runtime";
import {
  FONT_BODY,
  FONT_DISPLAY,
  TEXT_COLOURS,
  createTextField,
} from "./textStyle";

type LabelColour = "white" | "aqua";
type LabelAlign = "left" | "center" | "right";

type LabelValue = number | string;

/** Fixed caption, or one derived from the `extraValue` the label was built with. */
type LabelText = string | ((extra: LabelValue) => string);

/** One HUD/menu caption: what it says, how it is laid out, and how long it lives. */
interface LabelSpec {
  readonly text: LabelText;
  /** defaults to `"white"` */
  readonly colour?: LabelColour;
  /** frames before the caption starts fading out; 0 never fades on its own */
  readonly lifeLimit?: number;
  /** 1-based index into `GLOW_COLOURS`; absent means no glow */
  readonly glow?: number;
  /** overrides the default `"TRS Million"` */
  readonly font?: string;
  /** defaults to 400 */
  readonly width?: number;
  /** defaults to 60 */
  readonly height?: number;
  /** defaults to 22 */
  readonly size?: number;
  /** defaults to `"center"` */
  readonly align?: LabelAlign;
  readonly leading?: number;
}

const GLOW_COLOURS: readonly number[] = [6291, 13303808];

/** Every caption `Label` can render, keyed by the `id` it is constructed with. */
const LABEL_SPECS: Readonly<Record<number, LabelSpec>> = {
  0: { text: "SCORE", width: 92, height: 64 },
  1: { text: missionLabel, colour: "aqua", lifeLimit: 43, glow: 1, size: 32 },
  2: { text: "READY?", colour: "aqua", lifeLimit: 35, glow: 1, size: 48 },
  3: {
    text: missionCompleteLabel,
    colour: "aqua",
    lifeLimit: 120,
    glow: 1,
    size: 32,
  },
  4: {
    text: "MISSION FAILED!",
    colour: "aqua",
    lifeLimit: 90,
    glow: 1,
    size: 32,
  },
  5: {
    text: missionUnlockedLabel,
    colour: "aqua",
    lifeLimit: 120,
    glow: 1,
    size: 32,
  },
  6: {
    text: "DATA CHIP LOCATED!",
    colour: "aqua",
    lifeLimit: 90,
    glow: 1,
    size: 32,
  },
  7: {
    text: "E.CRUSHER AQUIRED!",
    colour: "aqua",
    lifeLimit: 120,
    glow: 1,
    size: 32,
  },
  8: {
    text: "G.SLICER AQUIRED!",
    colour: "aqua",
    lifeLimit: 120,
    glow: 1,
    size: 32,
  },
  9: {
    text: "MEGATRON AQUIRED!",
    colour: "aqua",
    lifeLimit: 120,
    glow: 1,
    size: 32,
  },
  10: {
    text: "BARRIER WING AQUIRED!",
    colour: "aqua",
    lifeLimit: 120,
    glow: 1,
    size: 32,
  },
  11: {
    text: "HIGH SCORE:",
    colour: "aqua",
    size: 24,
    width: 250,
    height: 30,
    align: "left",
  },
  12: {
    text: "HIGHSCORE",
    colour: "aqua",
    size: 24,
    width: 250,
    height: 30,
    align: "right",
  },
  13: {
    text: "MISSION SELECT",
    colour: "aqua",
    size: 24,
    width: 350,
    height: 30,
    align: "right",
  },
  14: {
    text: "AREA",
    colour: "aqua",
    font: FONT_BODY,
    size: 16,
    width: 176,
    height: 80,
    align: "right",
    leading: -8,
  },
  15: {
    text: "ITEM",
    colour: "aqua",
    size: 24,
    width: 440,
    height: 30,
    align: "left",
  },
  16: { text: "LOADING" },
  20: { text: "FIGHT!!", colour: "aqua", lifeLimit: 90, glow: 1, size: 32 },
  21: { text: "GAME OVER!", colour: "aqua", glow: 1, size: 32 },
  22: {
    text: (extra) => "WAVE " + extra,
    colour: "aqua",
    lifeLimit: 90,
    glow: 1,
    size: 32,
  },
  23: { text: "NEW HIGH SCORE!", glow: 2, size: 32, width: 720 },
};

/** A single-line caption on the HUD, a menu or the mission banner. */
export class Label extends MovieClip {
  private readonly format = new TextFormat();
  private alterableText!: TextField;
  private lifeLimit = 0;
  private life = 0;
  private fading = false;
  private alphaCurrent = 100;

  constructor(id = 0, x = 0, y = 0, extraValue: LabelValue = 0) {
    super();
    this.x = x;
    this.y = y;
    this.format.bold = true;
    this.format.font = FONT_DISPLAY;
    // ids with no entry (17-19) build an empty, textless clip, as the original did
    const label = LABEL_SPECS[id];
    if (label == null) {
      return;
    }
    if (label.font != null) {
      this.format.font = label.font;
    }
    this.lifeLimit = label.lifeLimit ?? 0;
    this.generateText(label, extraValue);
    if (label.glow != null) {
      this.filters = [
        new GlowFilter(GLOW_COLOURS[label.glow - 1], 1, 4, 4, 4, 1),
      ];
    }
  }

  /** Returns 1 once the caption has faded out and can be removed. */
  update(): number {
    let returnAction = 0;
    if (!this.fading) {
      if (this.lifeLimit > 0) {
        ++this.life;
        if (this.life >= this.lifeLimit) {
          this.fading = true;
        }
      }
    } else if (this.alphaCurrent > 0) {
      this.alphaCurrent -= 10;
      this.alpha = this.alphaCurrent * 0.01;
      if (this.alphaCurrent === 0) {
        returnAction = 1;
      }
    }
    return returnAction;
  }

  updateText(newText: string): void {
    this.alterableText.text = newText;
  }

  private generateText(label: LabelSpec, extraValue: LabelValue): void {
    this.format.align = label.align ?? "center";
    if (label.leading != null) {
      this.format.leading = label.leading;
    }
    this.format.color = TEXT_COLOURS[label.colour ?? "white"];
    this.format.size = label.size ?? 22;
    const t = createTextField(this, this.format, {
      width: label.width ?? 400,
      height: label.height ?? 60,
      text:
        typeof label.text === "function" ? label.text(extraValue) : label.text,
    });
    this.alterableText = t;
  }
}

function missionLabel(extra: LabelValue): string {
  if (typeof extra === "string") {
    if (extra === "TRAINING" || extra === "SURVIVAL") return extra;
    return extra === "X" ? "MISSION X" : "MISSION  " + extra;
  }
  if (extra >= 32) return "SURVIVAL";
  if (extra === 31) return "MISSION X";
  return extra > 0 ? "MISSION  " + extra : "TRAINING";
}

function missionCompleteLabel(extra: LabelValue): string {
  const mission = missionLabel(extra);
  return mission === "TRAINING" || mission === "MISSION X"
    ? mission + " COMPLETE!"
    : mission + "  COMPLETE!";
}

function missionUnlockedLabel(extra: LabelValue): string {
  if (extra === "X" || extra === 31) return "MISSION  X  UNLOCKED!";
  return missionLabel(extra) + "  UNLOCKED!";
}
